import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';

import { Rule, RulesMap } from '../../types/Rules';
import { useVoucher } from '../../context/VoucherProdiver';
import { getCityById, getLineById } from '../../apis';
import { getCachedBrands } from '../VoucherForm/Rules/Brands/brandsCache';
import { Line } from '../../types/Line';
import { Brand } from '../../types/Brand';
import { City } from '../../types/City';
import { deriveCreationTimezoneOffset } from '../../helpers/dateTime';
import { useRuleEntities } from './useRuleEntities';

import InfoDisplay, { InfoDisplayProps } from './InfoDisplay';
import { Currency } from '@flixbus/honeycomb-react';
import styles from './voucher-info.module.scss';

const isUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

const LINE_RULE_TYPES: Rule['type'][] = ['line_blacklist', 'line_whitelist'];
const BRAND_RULE_TYPES: Rule['type'][] = ['brand_allowlist', 'brand_disallowlist'];
const RELATION_RULE_TYPES: Rule['type'][] = ['allowed_relations', 'forbidden_relations'];

export interface RuleDisplayProps {
  title: string;
  fields: Rule['type'][];
}

export interface RuleDisplayViewProps extends RuleDisplayProps {
  rules: Rule[] | undefined;
  layout?: InfoDisplayProps['layout'];
}

export const RuleDisplayView = ({ title, fields, rules, layout }: RuleDisplayViewProps) => {
  const getLineValues = useCallback(
    (rule: Rule) => ('lines' in rule ? (rule as Rule & { lines: string[] }).lines : []),
    [],
  );

  const fetchLines = useCallback(
    (uuids: string[]) => Promise.all(uuids.map(id => getLineById(id))),
    [],
  );

  const getLineKey = useCallback((line: Line) => line.lineId, []);

  const getBrandValues = useCallback(
    (rule: Rule) => ('brands' in rule ? (rule as Rule & { brands: string[] }).brands : []),
    [],
  );

  const fetchBrands = useCallback(
    (uuids: string[]) =>
      getCachedBrands().then(allBrands => allBrands.filter(b => uuids.includes(b.uuid))),
    [],
  );

  const getBrandKey = useCallback((brand: Brand) => brand.uuid, []);

  const getCityValues = useCallback(
    (rule: Rule) =>
      'relations' in rule && rule.level === 'CITY'
        ? rule.relations.flatMap(relation => [relation.from, relation.to])
        : [],
    [],
  );

  const fetchCities = useCallback(
    (uuids: string[]) => Promise.all(Array.from(new Set(uuids)).map(id => getCityById(id))),
    [],
  );

  const getCityKey = useCallback((city: City) => city.id, []);

  const { entityMap: linesMap, loading: loadingLines } = useRuleEntities<Line>({
    rules,
    fields,
    ruleTypes: LINE_RULE_TYPES,
    getValues: getLineValues,
    fetchEntities: fetchLines,
    getKey: getLineKey,
  });

  const { entityMap: brandsMap, loading: loadingBrands } = useRuleEntities<Brand>({
    rules,
    fields,
    ruleTypes: BRAND_RULE_TYPES,
    getValues: getBrandValues,
    fetchEntities: fetchBrands,
    getKey: getBrandKey,
  });

  const { entityMap: citiesMap, loading: loadingCities } = useRuleEntities<City>({
    rules,
    fields,
    ruleTypes: RELATION_RULE_TYPES,
    getValues: getCityValues,
    fetchEntities: fetchCities,
    getKey: getCityKey,
  });

  const renderDate = useMemo(
    () =>
      (str: string, isEndOfDay = false) => {
        const { offsetString, offsetMinutes } = deriveCreationTimezoneOffset(str, isEndOfDay);
        const dt = DateTime.fromISO(str, { zone: 'utc' }).plus({ minutes: offsetMinutes });
        return `${dt.toFormat('dd.MM.yyyy')}, ${dt.toFormat('HH:mm')} ${offsetString}`;
      },
    [],
  );

  if (!rules || rules.length === 0) return null;

  const renderRuleValue = (
    ruleType: Rule['type'],
    fieldKey?: 'start' | 'end',
    ruleIndex?: number,
  ) => {
    const matchingRules = rules.filter(r => r.type === ruleType);

    const rule =
      ruleType === 'blackout_period' && ruleIndex !== undefined
        ? matchingRules[ruleIndex]
        : matchingRules[0];

    if (!rule) return '-';

    switch (rule.type) {
      case 'allowed_countries':
        return rule.countries.join(', ');
      case 'forbidden_countries':
        return rule.countries.join(', ');
      case 'allowed_platforms':
        return rule.platforms.join(', ');
      case 'travel_period':
      case 'blackout_period':
        if (fieldKey === 'start') {
          return rule.start ? renderDate(rule.start, false) : 'N/A';
        } else if (fieldKey === 'end') {
          return rule.end ? renderDate(rule.end, true) : 'N/A';
        }
        return `${rule.start ? renderDate(rule.start, false) : 'N/A'} to ${rule.end ? renderDate(rule.end, true) : 'N/A'}`;
      case 'direct_ride':
        return 'Direct Ride Only';
      case 'line_blacklist':
      case 'line_whitelist':
        if (loadingLines) return 'Loading...';
        return rule.lines
          .map(lineValue => {
            if (isUUID(lineValue)) {
              const line = linesMap[lineValue];
              return line ? `${line.code} - ${line.title}` : lineValue;
            }
            return lineValue;
          })
          .join(', ');
      case 'allowed_transport_type':
        return (
          (rule.transportTypes ?? [])
            .map(t => t.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()))
            .join(', ') || '-'
        );
      case 'brand_allowlist':
      case 'brand_disallowlist':
        if (loadingBrands) return 'Loading...';
        return rule.brands
          .map(brandUuid => {
            const brand = brandsMap[brandUuid];
            return brand ? brand.name : brandUuid;
          })
          .join(', ');
      case 'allowed_relations':
      case 'forbidden_relations': {
        if (rule.relations.length === 0) return '-';
        if (rule.level === 'CITY' && loadingCities) return 'Loading...';
        const getName = (code: string) =>
          rule.level === 'CITY' ? (citiesMap[code]?.name ?? code) : code;
        return (
          <span className={styles.stackedList}>
            {rule.relations.map(relation => (
              <span key={relation.id}>
                {getName(relation.from)} → {getName(relation.to)}
              </span>
            ))}
          </span>
        );
      }
      case 'minimum_tickets_amount':
        return `${rule.count}`;
      case 'maximum_value':
        return <Currency value={rule.value.amount} code={rule.value.currency} />;
      case 'minimum_value':
        return <Currency value={rule.value.amount} code={rule.value.currency} />;
      case 'offline_sales':
        return 'Offline Sales Only';
      case 'weekdays_travel':
        return rule.weekDays.join(', ');
      case 'free_ancillaries':
        return rule.ancillaries.join(', ');
      case 'allowed_customer':
        if (rule.identifiers.length === 0) return '-';
        return (
          <span className={styles.stackedList}>
            {rule.identifiers.map(id => (
              <span key={`${id.type}-${id.value}`}>
                {id.type}: {id.value}
              </span>
            ))}
          </span>
        );
      default:
        return '-';
    }
  };

  const expandedFields = fields.flatMap(f => {
    if (f === 'travel_period') {
      return [
        { label: `${RulesMap[f]} since`, value: renderRuleValue(f, 'start') },
        { label: `${RulesMap[f]} until`, value: renderRuleValue(f, 'end') },
      ];
    }
    if (f === 'blackout_period') {
      const blackoutRules = rules.filter(r => r.type === 'blackout_period');

      return blackoutRules.map((_, index) => ({
        label: '',
        value: (
          <>
            <span className={styles.label}>From</span>{' '}
            <strong>{renderRuleValue(f, 'start', index)}</strong>{' '}
            <span className={styles.label}>To</span>{' '}
            <strong>{renderRuleValue(f, 'end', index)}</strong>
          </>
        ),
        skipBoldWrapper: true,
      }));
    }
    return [{ label: RulesMap[f], value: renderRuleValue(f) }];
  });

  return <InfoDisplay title={title} fields={expandedFields} layout={layout} />;
};

const RuleDisplay = (props: RuleDisplayProps) => {
  const {
    data: { rules },
  } = useVoucher();

  return <RuleDisplayView {...props} rules={rules} />;
};

export default RuleDisplay;
